/**
 * The thread a page reads (#115): a job and everything said about it, or
 * one arrival about no job drawn as a job of one.
 *
 * Two routes open the same page (`components/arrivals/Record`) — the
 * job, and the arrival — and each carried its own copy of reading it:
 * the fetch, the wait for its recordings, the abort, the edit folded
 * back into what is drawn, and what deleting a call means. The copies
 * had already drifted in the one place they differ, which is what a
 * delete leaves behind. That is all here now, under one address, and a
 * route says which address it was opened on and nothing else.
 *
 * What a delete leaves: an arrival about no job is the thread, so it
 * goes and the reader is sent back. On a job, the arrival the job was
 * drafted from is the thread too, and the job goes whole, as the list
 * deletes it; a follow-up goes on its own and the job is read again
 * without it, and a job with nothing left on it, or one that cannot be
 * read back, sends the reader back.
 */
import { useEffect, useState } from "react";
import {
  deleteArrivals,
  getCallArrival,
  getFormArrival,
  getJob,
  type CallArrival,
  type FormArrival,
  type Job,
  type JobEntry,
} from "./arrivals-api";
import { errorMessage } from "./error-message";
import { readRecordings } from "./recordings";

/** Where the page was opened: a job by its Backend Ref, or one arrival. */
export type ThreadAddress =
  | { job: string }
  | { kind: string | undefined; id: string };

/** What reading an address came to. */
type Read = { job: Job } | { movedTo: string };

async function readThread(
  address: ThreadAddress,
  signal: AbortSignal,
): Promise<Read> {
  let job: Job;
  if ("job" in address) {
    job = await getJob(address.job, signal);
  } else {
    const { kind, id } = address;
    if (kind !== "call" && kind !== "form") {
      throw new Error("There is nothing here.");
    }
    const arrival =
      kind === "call"
        ? await getCallArrival(id, signal)
        : await getFormArrival(id, signal);
    // An arrival about a job is read on the job, with every other call
    // about it in one thread; its recordings are waited for there.
    if (arrival.backend_ref) return { movedTo: arrival.backend_ref };
    job = jobOfOne(arrival);
  }
  // The page is drawn once its recordings are in hand, so every player
  // on it is ready the moment it is seen; a file that fails, or one
  // still moving at the cap, does not hold it up.
  await readRecordings(
    job.thread
      .filter((e) => e.kind === "call" && e.has_recording)
      .map((e) => e.id),
  );
  return { job };
}

export interface Thread {
  job: Job | null;
  loadError: string | null;
  /** The job an arrival is about, where it was opened on that arrival. */
  movedTo: string | null;
  /** One call in the thread changed on the page: the thread says so. */
  edited: (id: string, next: Partial<JobEntry>) => void;
  /**
   * One arrival in the thread is to go (#158). Rejects with what went
   * wrong, which the confirm shows in place; `onGone` is called when
   * nothing is left to read.
   */
  removed: (entry: JobEntry) => Promise<void>;
}

export function useThread(address: ThreadAddress, onGone: () => void): Thread {
  const [job, setJob] = useState<Job | null>(null);
  const [movedTo, setMovedTo] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const key =
    "job" in address ? `job:${address.job}` : `${address.kind}:${address.id}`;

  useEffect(() => {
    const ac = new AbortController();
    readThread(address, ac.signal)
      .then((read) => {
        if (ac.signal.aborted) return;
        if ("movedTo" in read) setMovedTo(read.movedTo);
        else setJob(read.job);
        setLoadError(null);
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (ac.signal.aborted) return;
        setLoadError(errorMessage(err));
      });
    return () => ac.abort();
    // The address is read by its key: a new object for the same place
    // is the same thread.
  }, [key]);

  const edited = (id: string, next: Partial<JobEntry>) =>
    setJob(
      (prev) =>
        prev && {
          ...prev,
          thread: prev.thread.map((e) => (e.id === id ? { ...e, ...next } : e)),
        },
    );

  const removed = async (entry: JobEntry) => {
    const ref = job?.backend_ref ?? "";
    if (ref && entry.is_inquiry) {
      await deleteArrivals([], [ref]);
    } else {
      await deleteArrivals([{ kind: entry.kind, id: entry.id }]);
    }
    const next =
      ref && !entry.is_inquiry ? await getJob(ref).catch(() => null) : null;
    if (!next || next.thread.length === 0) {
      onGone();
      return;
    }
    setJob(next);
  };

  return { job, loadError, movedTo, edited, removed };
}

/**
 * One arrival about no job, as a job of one.
 *
 * The two reads answer with different shapes — a job carries its own
 * name, its quote number and the thread of everything about it, and an
 * arrival carries itself — and this is the whole of the difference,
 * held in one place so the page below it never asks which read it came
 * from. Nothing here invents a fact: an arrival about no job has no
 * Backend Ref, and everything else on it is the arrival's own.
 */
export function jobOfOne(arrival: CallArrival | FormArrival): Job {
  const call = arrival.kind === "call" ? arrival : null;
  const form = arrival.kind === "form" ? arrival : null;
  const entry: JobEntry = {
    kind: arrival.kind,
    id: arrival.id,
    at: arrival.at,
    arrived: arrival.arrived,
    who: arrival.who,
    who_org: arrival.who_org,
    from_e164: arrival.from_e164,
    email: call ? call.email : (form?.contact_email ?? ""),
    summary: arrival.summary,
    transcript: call?.transcript ?? "",
    turns: call?.turns ?? null,
    has_recording: arrival.has_recording,
    duration_secs: call?.duration_secs ?? null,
    status: call?.status ?? "",
    discard_reason: call?.discard_reason ?? "",
    left_message: call?.left_message ?? false,
    message_for: call?.message_for ?? "",
    is_inquiry: arrival.became_inquiry,
    said: arrival.said,
  };
  return {
    backend_ref: "",
    // No job to be named by its event: the heading is whoever came in.
    title: "",
    who: arrival.who,
    quote_number: arrival.quote_number,
    workspace_url: arrival.workspace_url,
    rental_system: arrival.rental_system,
    inquiry_status: arrival.inquiry_status,
    failure_reason: arrival.failure_reason,
    inquiry: {
      kind: arrival.kind,
      id: arrival.id,
      // A form submission is its own enquiry; a call names the one it
      // was written onto.
      inquiry_id: call ? call.inquiry_id : arrival.id,
      at: arrival.at,
      contact_email: call ? call.email : (form?.contact_email ?? ""),
      contact_phone: arrival.from_e164,
      get_back_to: arrival.get_back_to,
      rental_system: arrival.rental_system,
      asked_for: arrival.asked_for,
      facts: arrival.facts,
      answers: arrival.answers,
    },
    thread: [entry],
  };
}
